/* 
  ©2016-2017 EdgeVerve Systems Limited (a fully owned Infosys subsidiary),
  Bangalore, India. All Rights Reserved. */

import '@polymer/paper-toast/paper-toast.js';
import '@polymer/paper-button/paper-button.js';
import 'oe-i18n-msg/oe-i18n-msg.js';
import { html, Polymer } from '@polymer/polymer/polymer-legacy.js';

/**
 * `<app-message-handler>` is an element for handling message/warning/error display using `<paper-toast>`.
 * The component can be placed anywhere on the main application page and all message display will be handled
 * automatically.
 * <app-message-handler duration="2000" persist-on="error"></app-message-handler>
 * ### Show a toast
 * Within a polymer element
 * this.fire('oe-show-message','Hello World!');
 * From javascript
 * var event = new CustomEvent('oe-show-message', {detail: "Hello World!"});
 * window.dispatchEvent(event);
 * For warning and error messages
 * this.fire('oe-show-warning','This is a warning message!');
 * this.fire('oe-show-error','Oh snap! something went terribly wrong');
 * Toast that stays forever
 * this.fire('oe-show-message',{
 * message:'This message stays until the next toast',
 * forever:true
 * });
 * Toasts to be explicitly dismissed
 * When persist-on="error,warning" is provided, all error and warning messages will persist and OK button must be
 * pressed to dismiss the toast. An ok callback can be provided which is invoked when user presses OK.
 * this.fire('oe-show-error', {
 * message:'You must login before posting data.',
 * ok: goToLoginPage
 * });
 * ### i18n
 * If passed in message object has a `code` property, it is transformed for i18n. This means, you can pass the error-codes/message-codes as `msgData.code` and `app-message-handler` will translate them into current language. When a translation is not found, `msgData.message` is displayed (if present), otherwise `msgData.code` (i.e. the error-code/message-code) is displayed as it is.
 * this.fire('oe-show-error', {
 * code:'invalid-credentials',
 * message: 'Invalid Credentials'
 * });
 * Now the `app-message-handler` will look for a translation for 'invalid-credentials' and will display if it is found. If the
 * translation is not defined then 'Invalid Credentials' will be displayed.
 * ### Confirmation window
 * Confirmation toast, takes ok and cancel callbacks.
 * this.fire('oe-show-confirmation', {
 * message:'Record will be permanently removed. Are you sure?',
 * ok: okCallback,
 * cancel:cancelCallback
 * });
 * ### Styling
 * The following custom properties and mixins are available for styling:
 * Custom property | Description | Default
 * ----------------|-------------|----------
 * `--oe-message-success-background` | The background color of the success | `#78ab46`
 * `--oe-message-warning-background` | The background color of the warning | `#ffa000`
 * `--oe-message-error-background`   | The background color of the error   | `#d32f2f`
 * @customElement
 * @polymer
 * @demo node_modules/speedboat_ui/elements/demo/app-message-handler-demo.html
 * @summary Technical component
*/
const template = html`
  <style include="login-styles">
      .success {
        background: var(--oe-message-success-background,var(--primary-light-color));
        color:var(--success-label-color);
        box-shadow:0px 0px 8px 0px var(--snackbar-background-success-color);
      }
  
      .error {
        background: var(--oe-message-error-background,var(--primary-light-color));
        color:var(--critical-label-color);
        box-shadow:0px 0px 8px 0px var(--snackbar-background-critical-color);
        /*Changes Applied according to UX-Specification*/
      }
  
      #errMsg,#msg
      {
         color:var(--primary-dark-color);
         font-size:15px;
         font-family:var(--regular-font);
      }
  
      .warning {
        background: var(--snackbar-background-warning-color, var(--primary-light-color));
      }
  
      
      .message {
        background: var(--oe-message-error-background,var(--primary-light-color));
        color:var(--secondary-dark-color);
        box-shadow:0px 0px 8px 0px var(--brand-color-200);
      }
      paper-toast {
        @apply --app-message-handler-toast-mixin;
        
      }
  
      /* deprecated in Polymer-3 ::shadow */
      /*
      paper-toast::shadow{
       padding: 7px 24px !important;
      }
      */
  
    #closableOK, #closableOKMulti, #action{
      font-weight: 500;
      color:var(--brand-color-200);
      padding:0px;
    }
    #closableOK, #action{
      padding-top:2.5px;
      float: right;
    }
  
      
     #translator {
        display: none;
      }
  
      #placeholderDiv{
       height:14px;
      }
  
      :dir(ltr) #ironIcon
      {
        margin-right: 5px;
      }
    :dir(rtl) #ironIcon
      {
        margin-left: 5px;
      }
      @media all and (-ms-high-contrast:none)
      {
          paper-toast {
              max-width: none !important;
          }
      }
      #errMsg{
        margin-left:6px;
      }
  
      paper-toast{
        padding: 16px 12px;
        top: auto !important;
        bottom:40%;
        left:0;
        max-height: 100% !important;
        -webkit-animation: fadein 1.5s, fadeout 1s 3s;
        animation: fadein 1.5s, fadeout 1s 3s; 
        border-radius: 10px; 
      }
      @-webkit-keyframes fadein {
        from {opacity: 0;}
        to {opacity: 0.8;}
    }
    
    @keyframes fadein {
        from {opacity: 0;}
        to {opacity: 0.8;}
    } 
    .errorFontStyle {
      color: black;
      font-weight: bold;
    }
    .padding-t-b {
      padding-left: 2em;
      padding-bottom: 0.8em;
      padding-top: 0.7em;
    }
    .okButtonStyle {
      text-align: center;
    }
    .toastHeight{
      max-height:fit-content !important
    }
    .ironListNoScroll {
      overflow-y: inherit !important;
    }
    .outlineOffset:focus{
	outline:2px solid #0075da;
	outline-offset : "3px !important";
    }
    .single-line{
      min-height: 54px;
      min-width: 344px;

    }
    .multi-line{
      min-height: 136px;
      min-width: 395px;

    }
    iron-icon{
      height:20px;
      width:20px;
  }
  
  </style>

<app-media-query phone="{{phone}}" tablet="{{tablet}}" desktop="{{desktop}}"></app-media-query>
    <oe-i18n-msg id="translator"></oe-i18n-msg>
    <paper-toast role="alert"  class="single-line" id="toast" with-backdrop no-auto-focus="{{noAutoFocus}}" horizontal-align="center"  aria-atomic="true" aria-live="assertive">
       <iron-icon icon="{{icon}}"></iron-icon>
       <span  id="msg">{{text}}</span>
       <!-- Added aria hidden attribute to hide the button in accessbility reader when there is no content -->
       <paper-button class="outlineOffset" id="action" on-tap="_choiceMade" aria-hidden=false>
         <div id="placeholderDiv"></div>
       </paper-button>
    </paper-toast>

    <paper-toast role="alert" class="single-line"  no-cancel-on-outside-click$="_closeToast" horizontal-align="center" duration=0 id="closable" no-auto-focus="{{noAutoFocus}}" aria-atomic="true" aria-live="assertive">
      <div class="display-flex justify-content-space-bwn">
	  <div class="display-flex">
		  <div>
			<iron-icon id="ironIcon" class="mdl-grid padding-zero" icon="{{icon}}"></iron-icon>
		  </div>      
		  <div id="errMsg">{{text}}</div>
	  </div>
        <div id="inlineCloseBtn">
          <paper-button class="outlineOffset" id="closableOK" on-tap="_closeToast">
              <oe-i18n-msg msgid="b_OK"></oe-i18n-msg>
          </paper-button>
        </div>
      </div>
      <div id="multiLineCloseBtn" class="mdl-grid padding-b-zero">
          <paper-button class="outlineOffset" id="closableOK" on-tap="_closeToast">
              <oe-i18n-msg msgid="b_OK"></oe-i18n-msg>
          </paper-button>
        </div>
    </paper-toast>

    <!-- MVF 294869: This paper toast is added to show list of errors for handling multiple error entries. -->
    <paper-toast role="alert" class="multi-line" class="toastHeight"  no-cancel-on-outside-click$="_closeToastMulti" horizontal-align="center" duration=0 id="closableMultiLine" no-auto-focus="{{noAutoFocus}}" aria-atomic="true" aria-live="assertive">
      <div class="position-relative">
          <iron-icon id="ironIcon" icon="{{icon}}"></iron-icon>
          <span role="heading"><oe-i18n-msg msgid="l_errors"class="errorFontStyle"></oe-i18n-msg></span>
          <div class="padding-t-b">
            <template is="dom-repeat" items="{{msgList}}">
              <div id="errMsg">{{item}}</div>
            </template>
          </div>
          <div class="okButtonStyle" >
            <paper-button class="outlineOffset" id="closableOKMulti" on-tap="_closeToastMulti">
              <oe-i18n-msg msgid="b_OK"></oe-i18n-msg>
            </paper-button>
          </div>
      </div>
    </paper-toast>
      <paper-toast role="alert" class="multi-line" with-backdrop id="confirm" horizontal-align="center" no-auto-focus="{{noAutoFocus}}" aria-atomic="true" aria-live="assertive">
        <paper-button class="outlineOffset" id="cancel" on-tap="_choiceMade">
          <oe-i18n-msg msgid="b_Cancel"></oe-i18n-msg>
        </paper-button>
        <paper-button class="outlineOffset" id="ok" on-tap="_choiceMade">
          <oe-i18n-msg msgid="b_OK"></oe-i18n-msg>
        </paper-button>
      </paper-toast>`;
Polymer({
  is: 'app-message-handler',
  _template: template,
  properties: {

    /* duration for the toast display */
    duration: {
      type: Number,
      value: 5000
    },

    /* fit entire screen width at bottom */
    fitBottom: {
      type: Boolean
    },

    /* comma separated list of messaging events when the toast should persist and
     * should not auto hide after `duration`
     */
    persistOn: {
      type: String
    },
    /* 
     Icon to be provided 
     */
    icon: {
      type: String
    },
    /*
    noAutoFous is passed to paper-toast to set focus on toast for DDA. By nature toast does not steal focus.
    noAutoFous is set to false in paper-toast.
    */
    noAutoFocus: {
      type: Boolean,
      value: false
    },
    //MVF 294869: This property is added to store array of errors for handling multiple error entries.
    msgList: {
      type: Array
    },
    //MVF 294869: This property is added to store string from array of errors for DDA for handling multiple error entries.
    msgListString: {
      type: String
    },
    /*This element indicates that the device is mobile or not*/
    phone: {
      type: Boolean,
      notify: true
    }
  },
  /**
   * Method to show the toast message
   * @param {string} type
   * @param {object} data
  **/
  showToast: function (type, data) {
    var toast = this.$.toast;
    var msgCode = data.code || data.message || data;
    //use data.message also, in case somebody is still passing only message as messageCode and expecting translation

    var placeholders = data.placeholders;
    var i18nMsg = this.$.translator.getInterpolatedMsg(msgCode, placeholders);
    var translatedMsg = i18nMsg || data.message || msgCode;

    var duration = data.forever ? 0 : this.duration;

    if (type === 'confirm') {
      toast = this.$.confirm;
    } else if (type == "error" && typeof (translatedMsg) === "object" && translatedMsg.length) {
      //MVF 294869: This condition is added for handling multiple error entries in response. 
      toast = this.$.closableMultiLine;
      this.$.closableOKMulti.cb = data.ok;
      duration = 0;
    } else if (this.persistOn && this.persistOn.indexOf(type) >= 0 && data && !data.hasOwnProperty("actionName")) {
      toast = this.$.closable;
      this.$.closableOK.cb = data.ok;
      duration = 0;
      setTimeout(function () {
        this.$.closableOK.focus();
      }.bind(this), 500);

      if (this.phone && data.length > 30) {
        this.$.inlineCloseBtn.classList.add('display-none');
        this.$.multiLineCloseBtn.classList.add('content-align-center');
      } else {
          this.$.inlineCloseBtn.classList.remove('content-align-center');
        this.$.multiLineCloseBtn.classList.add('display-none');
      }
    }
    if (type === 'error') {
      this.set('icon', 'error-icon');
      if (typeof (translatedMsg) == "string") {
        if (data.hasOwnProperty("actionName")) {
          toast.removeAttribute("with-backdrop");
          this.$.action.cb = data.callBack;
          this.$.action.innerText = data.actionName;
          this.$.placeholderDiv.style.display = "none";
        }
        else {
          toast.setAttribute("with-backdrop", true);
          this.$.placeholderDiv.style.display = "block";
        }
      } else if (typeof translatedMsg == "object") {
        //MVF 294869: This condition is added for handling multiple error entries in response.
        this.msgList = translatedMsg;
        //trying to fix app-footer
        this.msgList = translatedMsg;
        this.msgListString = this.msgList.join(' ');
        toast.setAttribute("with-backdrop", true);
        this.$.placeholderDiv.style.display = "block";
      } else {
        toast.hide();
        this.set('text', getLabel('m_genericError'));
        toast.classList.remove('success');
        toast.classList.remove('warning');
        toast.classList.remove('error');
        toast.classList.remove('message');
        toast.classList.add(type);
        toast.show();
        return
      }
    }
    else if (type === 'message') {
      this.set('icon', 'info-icon');
      toast.removeAttribute("with-backdrop");
    }
    else if (type === 'success') {
      this.set('icon', 'successtick');
      toast.removeAttribute("with-backdrop");
      if (data.hasOwnProperty("actionName")) {
        this.$.action.cb = data.callBack;
        this.$.action.innerText = data.actionName;
        this.$.placeholderDiv.style.display = "none";
      }
      else {
        this.$.placeholderDiv.style.display = "block";
        this.$.action.setAttribute("aria-hidden", true);
      }
    }

    toast.hide();
    toast.set('duration', duration);
    this.set('text', translatedMsg);
    toast.classList.remove('success');
    toast.classList.remove('warning');
    toast.classList.remove('error');
    toast.classList.remove('message');
    toast.classList.add(type);
    toast.show();
  },
  /** 
   * Method to show the confirmation message
   * @param {object} data
  **/
  showConfirmation: function (data) {
    data.forever = true;

    this.$.ok.cb = data.ok;
    this.$.cancel.cb = data.cancel;


    this.showToast('confirm', data);

    //paper-toast has overriden [_renderOpen] method of IronOverlayBehavior
    //so backdrop never closes.
    //Execute the IronOverlayBehavior._renderOpen steps explicitly
    if (this.$.confirm.withBackdrop) {
      this.$.confirm.backdropElement.open();
    }
    this.$.confirm._finishRenderOpened();

  },
  /**
   * Method to be called based on option selected in confirmation message
   * @event
  **/
  _choiceMade: function (e) {
    if (e.currentTarget.cb) {
      e.currentTarget.cb();
    }

    this.$.ok.cb = undefined;
    this.$.cancel.cb = undefined;
    this.$.confirm.close();
    //Close toast after callback
    this.$.toast.close();

    //paper-toast has overriden [_renderClose] method of IronOverlayBehavior
    //so backdrop never closes.
    //Execute the IronOverlayBehavior._renderClose steps explicitly
    if (this.$.confirm.withBackdrop) {
      this.$.confirm.backdropElement.close();
    }
    if (this.$.toast.withBackdrop) {
      this.$.toast.backdropElement.close();
    }
    this.$.confirm._finishRenderClosed();
    //To render the toast actions
    this.$.toast._finishRenderClosed();
  },
  /** 
  * added this method to fix paper toast is getting hidden and blocking usre actions and affecting user experienece sometimes
  * @event
  **/
  _hidePaperToasts() {
    if (this.shadowRoot.querySelectorAll("paper-toast")) {
      var paperToastList = this.shadowRoot.querySelectorAll("paper-toast")
      paperToastList.forEach(function (item) {
        item.style.display = "none";
      }, this);
    }
  },
  /** 
   * Method to close the toast message
   * @event
  **/
  _closeToast: function (e) {
    e.stopPropagation();
    this.$.closable.toggle();
    if (e.target.cb) {
      e.target.cb();
      e.target.cb = undefined;
    }
    this._hidePaperToasts();
  },
  /** 
   * Method to be close multiple toast messages
   * @event
  **/
  _closeToastMulti: function (e) {
    e.stopPropagation();
    this.$.closableMultiLine.toggle();
    if (e.target.cb) {
      e.target.cb();
      e.target.cb = undefined;
    }
    this._hidePaperToasts();
  },
  attached: function () {
    if (this.fitBottom) {
      this.$.toast.classList.add('fit-bottom');
      this.$.closable.classList.add('fit-bottom');
      this.$.closableMultiLine.classList.add('fit-bottom');
      this.$.confirm.classList.add('fit-bottom');
    }
  },
  ready: function () {
    var self = this;
    window.addEventListener('oe-show-success', function (e) {
      self.showToast('success', e.detail);
    });
    window.addEventListener('oe-show-message', function (e) {
      self.showToast(e.detail.type || 'message', e.detail);
    });
    window.addEventListener('oe-show-error', function (e) {
      self.showToast('error', e.detail);
    });
    window.addEventListener('oe-show-warning', function (e) {
      self.showToast('warning', e.detail);
    });
    window.addEventListener('oe-show-confirm', function (e) {
      self.showConfirmation(e.detail);
    });
  }
});


